import type { ReactNode } from 'react';

import { Box, Flex, Text, useBreakpointValue, VisuallyHidden } from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@udacity/chakra-uds-icons';

import {
  type ComparisonCheckTone,
  type ComparisonStatusKind,
} from '~/features/homepage/constants/homepage-comparison-data';
import { getComparisonStatusPresentation } from '~/features/homepage/utils/get-comparison-status-presentation';

export type ComparisonStatusProps = {
  isCompact?: boolean;
  isLegend?: boolean;
  label: string;
  status: ComparisonStatusKind;
  tone: ComparisonCheckTone;
};

type ResponsiveSize = string | { base: string; md: string };

type StatusSizes = {
  brandCircle: ResponsiveSize;
  brandGlyph: ResponsiveSize;
  closeGlyph: ResponsiveSize;
  neutralCircle: ResponsiveSize;
  neutralGlyph: ResponsiveSize;
};

// UDS check/close glyphs fill roughly 45% of their 32px viewBox, and the UDS check is taller
// than the Figma check, so glyph boxes are sized to match the Figma glyph area.
const CELL_SIZES: StatusSizes = {
  brandCircle: { base: '26px', md: '30px' },
  brandGlyph: { base: '18px', md: '21px' },
  closeGlyph: { base: '14px', md: '17px' },
  neutralCircle: { base: '22px', md: '26px' },
  neutralGlyph: { base: '15px', md: '18px' },
};

const LEGEND_SIZES: StatusSizes = {
  brandCircle: '14px',
  brandGlyph: '10px',
  closeGlyph: '9px',
  neutralCircle: '14px',
  neutralGlyph: '10px',
};

export function ComparisonStatus({
  isCompact: isCompactOverride,
  isLegend = false,
  label,
  status,
  tone,
}: ComparisonStatusProps) {
  const breakpointIsCompact = useBreakpointValue({ base: true, md: false }, { fallback: 'md' });
  const isCompact = isCompactOverride ?? breakpointIsCompact;
  const presentation = getComparisonStatusPresentation(status, tone, isCompact);
  const sizes = isLegend ? LEGEND_SIZES : CELL_SIZES;

  if (presentation === 'partial-text') {
    return (
      <Text
        color='grey.500'
        fontFamily='body'
        fontSize='12px'
        fontWeight='600'
        letterSpacing='0.08em'
        lineHeight='16px'
        textTransform='uppercase'
      >
        {label}
      </Text>
    );
  }

  return (
    <Flex align='center' justify='center'>
      {presentation === 'brand-check' && (
        <StatusCircle bg='blue.100' size={sizes.brandCircle}>
          <CheckIcon boxSize={sizes.brandGlyph} color='blue.500' aria-hidden />
        </StatusCircle>
      )}
      {presentation === 'neutral-check' && (
        <StatusCircle bg='grey.200' size={sizes.neutralCircle}>
          <CheckIcon boxSize={sizes.neutralGlyph} color='grey.500' aria-hidden />
        </StatusCircle>
      )}
      {presentation === 'partial-icon' && <PartialCircle size={sizes.neutralCircle} />}
      {presentation === 'none' && (
        <StatusCircle bg='grey.200' size={sizes.neutralCircle}>
          <CloseIcon boxSize={sizes.closeGlyph} color='grey.500' aria-hidden />
        </StatusCircle>
      )}
      <VisuallyHidden>{label}</VisuallyHidden>
    </Flex>
  );
}

function StatusCircle({ bg, children, size }: { bg: string; children: ReactNode; size: ResponsiveSize }) {
  return (
    <Flex align='center' bg={bg} borderRadius='full' boxSize={size} flexShrink={0} justify='center'>
      {children}
    </Flex>
  );
}

function PartialCircle({ size }: { size: ResponsiveSize }) {
  return (
    <Box as='svg' boxSize={size} color='grey.500' viewBox='0 0 20 20' aria-hidden>
      <circle cx='10' cy='10' fill='white' r='9' stroke='currentColor' strokeWidth='1.5' />
      <path d='M10 1.5a8.5 8.5 0 0 1 0 17Z' fill='currentColor' />
    </Box>
  );
}
